import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";
import { Card } from "./ui";
import { ESTADOS, ESTADO_COLORS, ORIGENES, PERFILES, fmtMoney } from "./store";

const PALETTE = ["#2d5a3d", "#3a7a50", "#5a9a70", "#CBA153", "#A67C52", "#6B8F7D", "#8fb59c", "#d4b878", "#c4c9c0"];

const ChartCard = ({ title, sub, children, testid }) => (
  <Card className="p-5">
    <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
    <p className="mb-3 text-xs text-gray-400">{sub}</p>
    <div data-testid={testid} className="h-56">{children}</div>
  </Card>
);

export const Analytics = ({ data }) => {
  const { leads, apartments, tasks } = data;
  const total = Math.max(1, leads.length);

  const funnel = ESTADOS.map((e) => ({
    name: e,
    n: leads.filter((l) => l.estado === e).length,
    pct: Math.round((leads.filter((l) => l.estado === e).length / total) * 100),
  }));

  const origen = ORIGENES.map((o) => ({ name: o, value: leads.filter((l) => l.origen === o).length })).filter((o) => o.value > 0);
  const perfil = PERFILES.map((p) => ({ name: p, n: leads.filter((l) => l.perfil === p).length }));

  const months = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    months.push({ key, name: d.toLocaleDateString("es-ES", { month: "short" }), n: leads.filter((l) => (l.creado || "").startsWith(key)).length });
  }

  const cerrados = leads.filter((l) => l.estado === "Cerrado").length;
  const vendidos = apartments.filter((a) => a.estado === "Vendido").length;
  const metrics = [
    ["Total leads", leads.length],
    ["Leads calientes", leads.filter((l) => l.interes === "Caliente").length],
    ["Tasa de conversión", `${Math.round((cerrados / total) * 100)}%`],
    ["Valor pipeline activo", fmtMoney(leads.filter((l) => l.estado !== "Cerrado").reduce((s, l) => s + l.unidades * 59600, 0))],
    ["Apartamentos vendidos", `${vendidos}/18`],
    ["Ingresos por ventas", fmtMoney(vendidos * 59600)],
    ["Tareas pendientes", tasks.filter((t) => t.estado !== "Completada").length],
    ["Interacciones registradas", leads.reduce((s, l) => s + l.interacciones.length, 0)],
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <ChartCard title="Conversión por etapa" sub="% del total de leads en cada etapa" testid="chart-funnel">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={funnel} layout="vertical" margin={{ left: 20, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
              <Tooltip formatter={(v, n, p) => [`${p.payload.n} leads · ${v}%`, "Etapa"]} />
              <Bar dataKey="pct" radius={[0, 6, 6, 0]} maxBarSize={20}>
                {funnel.map((f) => <Cell key={f.name} fill={ESTADO_COLORS[f.name]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads por origen" sub="Canales de captación" testid="chart-origen">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={origen} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={3}>
                {origen.map((o, i) => <Cell key={o.name} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip />
              <Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads por perfil" sub="Tipología de inversor" testid="chart-perfil">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={perfil} margin={{ left: -14, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="n" name="Leads" fill="#2d5a3d" radius={[6, 6, 0, 0]} maxBarSize={44} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Nuevos leads por mes" sub="Tendencia de captación · últimos 6 meses" testid="chart-tendencia">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={months} margin={{ left: -18, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="n" name="Leads" stroke="#2d5a3d" strokeWidth={2.5} dot={{ r: 4, fill: "#CBA153" }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <Card className="p-5">
        <h4 className="text-sm font-semibold text-gray-900">Resumen de métricas</h4>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[480px] text-sm" data-testid="analytics-summary">
            <tbody>
              {metrics.map(([k, v]) => (
                <tr key={k} className="border-b border-gray-50 last:border-0">
                  <td className="py-2.5 text-gray-500">{k}</td>
                  <td className="py-2.5 text-right font-semibold text-gray-900">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
